/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import { NOT_APPLICABLE } from '@config/constants/common';
import { useValidImage } from '@hooks/useValidImage';
import {
	Avatar,
	Box,
	Card,
	Flex,
	Group,
	Image,
	NumberFormatter,
	Tooltip,
} from '@mantine/core';
import { IconQuestionMark } from '@tabler/icons-react';
import { truncateString } from '@utils/functions';
import {
	ChangeEventHandler,
	MouseEventHandler,
} from 'react';
import { useTranslation } from 'react-i18next';
import { IconProjectLocation } from '../IconProjectLocation/IconProjectLocation';
import { NumberInput } from '../NumberInput';
import { StatusSwitch } from '../StatusSwitch';
import { Text } from '../Text';
import { Title } from '../Title';
import classes from './AdditionalityOffsetProjectCard.module.css';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */

export type TProjectCardProps = {
	disableButton?: boolean;
	onAdd: () => void;
	type: 'Additionality' | 'additionality';
	projectType: string | undefined;
	name: string;
	cover: string | undefined;
	location: {
		countryCode: string;
		place: string;
		countryName: string;
	};
	availableStoc?: number;
	active: boolean;
	onStatusChange:
		| ChangeEventHandler<HTMLInputElement>
		| undefined;
	fullWidth?: boolean;
	supportedByLogo: string | undefined;
	availableStock: number;
	fundedPart: number;
	onClick: MouseEventHandler<HTMLDivElement> | undefined;
	showStatus: boolean;
	readonly: boolean;
	quantity?: string | number | undefined;
	onChangeQuantity?:
		| ((value: string | number) => void)
		| undefined;
	remainingPrice: number;
	price: number;
	onChangePrice: (val: number) => void;
	disableNumberInput?: boolean;
	isAdded?: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export const AdditionalityOffsetProjectCard: React.FC<
	TProjectCardProps
> = ({
	name,
	remainingPrice,
	active,
	onStatusChange,
	fullWidth,
	cover,
	supportedByLogo,
	location,
	projectType,
	fundedPart,
	onClick,
	showStatus = true,
	readonly,
	onChangePrice,
	price,
	disableButton,
	disableNumberInput,
	isAdded,
}) => {
	const { t } = useTranslation();

	const coverUrl = useValidImage(cover);
	/* ******************************** RENDERING ******************************* */
	return (
		<Card
			shadow="sm"
			pl={'var(--mantine-spacing-xxs)'}
			pr={'var(--mantine-spacing-xxs)'}
			pt={'var(--mantine-spacing-xxs)'}
			pb={'var(--mantine-spacing-sm)'}
			radius="var(--mantine-radius-lg)"
			withBorder
			miw={'300px'}
			maw={fullWidth ? undefined : '300px'}
			flex={fullWidth ? 1 : 0}
			className={classes.container}
			onClick={onClick}
		>
			<Box
				h={203}
				p={'var(--mantine-spacing-xs)'}
				style={{
					borderRadius: '24px',
					position: 'relative',
					background: `lightgray url(${coverUrl}) 50% / cover no-repeat`,
				}}
			>
				<Flex
					justify={
						supportedByLogo ? 'space-between' : 'flex-end'
					}
				>
					<img
						src={cover}
						alt={'alt'}
						style={{
							display: 'none',
						}}
					/>

					{supportedByLogo && (
						<Image
							radius={'xl'}
							src={supportedByLogo}
							h={48}
							w={48}
						/>
					)}
					<Box
						onClick={(e) => {
							e.stopPropagation();
						}}
					>
						{showStatus && (
							<StatusSwitch
								inActiveLabel={t('global.inactive')}
								activeLabel={t('global.active')}
								active={active}
								readonly={readonly}
								onChange={onStatusChange}
							/>
						)}
					</Box>
				</Flex>
			</Box>

			<Flex
				direction={'column'}
				mt="var(--mantine-spacing-sm)"
				pl={'var(--mantine-spacing-xxs)'}
				pr={'var(--mantine-spacing-xxs)'}
			>
				{/* Row 1 Project Address*/}
				<Flex
					gap={'var(--mantine-spacing-xs)'}
					mih={'20px'}
				>
					<Flex mih={'20px'}>
						<Group justify="center" gap={'5px'}>
							<Avatar
								radius={'xl'}
								src={`https://flagcdn.com/w40/${location?.countryCode.toLowerCase()}.png`}
								size={18}
							>
								<IconQuestionMark size={18} />
							</Avatar>
							<Tooltip
								label={location.countryName}
								disabled={
									location.countryName.length <= 11
								}
							>
								<Box>
									<Text
										variant={'caption'}
										c={
											'var(--mantine-color-igluSecondary-9)'
										}
									>
										{truncateString(
											location.countryName,
											12
										)}
									</Text>
								</Box>
							</Tooltip>
						</Group>
					</Flex>
					{location?.place != NOT_APPLICABLE && (
						<Flex
							style={{
								minHeight: '20px',
							}}
						>
							<Group gap={'5px'}>
								<IconProjectLocation color={'#617EEF'} />

								<Tooltip
									label={location.place}
									disabled={location.place.length <= 11}
								>
									<Box>
										<Text
											variant={'caption'}
											c={
												'var(--mantine-color-igluSecondary-9)'
											}
										>
											{truncateString(location.place, 12)}
										</Text>
									</Box>
								</Tooltip>
							</Group>
						</Flex>
					)}
					<Flex flex={1} justify={'end'} mih={'20px'}>
						<Text
							variant={'caption'}
							c={'var(--mantine-color-igluSecondary-9)'}
						>
							{t('global.fundedPart')}
						</Text>
					</Flex>
				</Flex>
				{/* Row 2 Project Value*/}
				<Flex gap={'var(--mantine-spacing-xs)'}>
					<Flex flex={1} justify={'end'} mih={'20px'}>
						<Title
							order={6}
							c={'var(--mantine-color-igluPrimary-9)'}
						>
							<NumberFormatter
								thousandSeparator=" "
								decimalSeparator=","
								value={fundedPart}
								decimalScale={2}
							/>
							<Text
								span
								variant={'caption'}
								c={'var(--mantine-color-igluPrimary-9)'}
							>
								{` €`}
							</Text>
						</Title>
					</Flex>
				</Flex>
				{/* Row 3 Project label*/}
				<Flex
					mt={'var(--mantine-spacing-xs)'}
					gap={'var(--mantine-spacing-xs)'}
				>
					<Flex flex={1} mih={'20px'}>
						<Text
							variant="caption"
							c={'var(--mantine-color-igluSecondary-9)'}
						>
							{t('placeholders.projectName')}
						</Text>
					</Flex>
				</Flex>
				{/* Row 4 Project name*/}
				<Flex gap={'var(--mantine-spacing-xs)'}>
					<Flex flex={1} mih={'20px'}>
						<Tooltip
							label={name}
							disabled={name ? name.length <= 29 : true}
						>
							<Box>
								<Text
									variant="subtitle1"
									c={
										'var(--mantine-color-igluTextPrimary-9)'
									}
								>
									{(name && truncateString(name, 30)) ||
										NOT_APPLICABLE}
								</Text>
							</Box>
						</Tooltip>
					</Flex>
				</Flex>
				{/* Row 5 Project type*/}
				<Flex
					gap={'var(--mantine-spacing-xs)'}
					mt={'var(--mantine-spacing-sm)'}
				>
					<Flex
						flex={1}
						style={{
							minHeight: '20px',
						}}
					>
						<Group gap={'5px'}>
							<Text
								variant={'caption'}
								c={'var(--mantine-color-igluSecondary-9)'}
							>
								{t(`global.projectType`)}
							</Text>
						</Group>
					</Flex>
					<Flex flex={1.5} mih={'20px'}>
						<Tooltip
							label={projectType && t(projectType)}
							disabled={
								projectType
									? t(projectType).length <= 23
									: true
							}
						>
							<Box>
								<Text
									data-testid="project-type"
									variant={'body2'}
									c={
										'var(--mantine-color-igluTextPrimary-9)'
									}
								>
									{projectType
										? truncateString(t(projectType), 24)
										: NOT_APPLICABLE}
								</Text>
							</Box>
						</Tooltip>
					</Flex>
				</Flex>

				<NumberInput
					disabled={disableNumberInput}
					min={0}
					clampBehavior="strict"
					label={`${t('global.allocationAmountIn')} €`}
					mt="24px"
					size="md"
					placeholder="0"
					value={price}
					defaultValue={0}
					unit="€"
					radius={'xl'}
					onChange={
						!disableButton ? onChangePrice : () => {}
					}
					styles={{
						input: {
							border: isAdded
								? '1.5px solid var(--Colors-Blue, #007AFF)'
								: '',
							backgroundColor: disableButton
								? '#F0F0F0'
								: undefined,
							color: disableButton
								? '#6C6C6C'
								: undefined,
							cursor: disableButton
								? 'not-allowed'
								: 'text',
						},
					}}
				/>

				{price > 0 && (
					<Flex justify="center" mt="8px">
						<Text variant="subtitle1" c={'#97AAC1'}>
							{`${t('global.remainingToAllocate')} : `}
							<Text
								span
								variant="subtitle1"
								c={'#007AFF'}
							>
								{`${remainingPrice} €`}
							</Text>
						</Text>
					</Flex>
				)}
			</Flex>
		</Card>
	);
};
